import { useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { emailAPI } from '@/services/email/email-api';
import { profileApi } from '@/api/profile';
import { usersApi } from '@/api/users';
import { toast } from 'sonner';

// Add interfaces at the top of the file
interface JobData {
  firstName: string;
  jobTitle: string;
  location: string;
  startDate: string;
  budgetRange: string;
  description: string;
  clientName: string;
  clientRating?: number;
  role?: string;
  jobId: string;
}

interface ApplicationData {
  jobTitle: string;
  clientName: string;
  bidAmount: number;
  timestamp: string;
  // Navigation IDs for clickable notifications
  job_id?: string;
  bid_id?: string;
}

interface BookingData {
  jobTitle: string;
  clientName: string;
  filmmakerName: string;
  projectDates: string;
  location: string;
  agreedAmount: number;
  role: string;
  isForFilmmaker: boolean;
  isContractOffer?: boolean; // When client signs first - offer to filmmaker
  isBookingConfirmed?: boolean; // When filmmaker signs - final confirmation
}

// Hook for welcome email after registration
export const useWelcomeEmail = () => {
  const { user, userType } = useAuth();

  const sendWelcomeEmail = useCallback(
    async (userData: {
      email: string;
      firstName: string;
      userType: string;
    }) => {
      try {
        const result = await emailAPI.sendWelcomeEmail(userData.email, {
          firstName: userData.firstName,
          userType: userData.userType,
        });

        if (result.success) {
          // Track account creation time for profile reminder logic
          const accountCreatedKey = `account_created_${user?.id}`;
          if (user?.id && !localStorage.getItem(accountCreatedKey)) {
            localStorage.setItem(accountCreatedKey, Date.now().toString());
          }
          console.log('Welcome email sent successfully');
        } else {
          console.error('Failed to send welcome email:', result.error);
        }
      } catch (error) {
        console.error('Error sending welcome email:', error);
      }
    },
    [user?.id]
  );

  return { sendWelcomeEmail };
};

// Hook for profile completion reminders
export const useProfileReminders = () => {
  const { user, userType } = useAuth();

  const checkProfileCompleteness = useCallback(async () => {
    if (!user || !userType || userType === 'admin') return;

    // ✅ REFACTORED: Use dedicated profile API instead of complex mixed logic
    try {
      const completenessResult = await profileApi.checkProfileCompleteness(
        user.id, 
        userType as 'filmmaker' | 'client'
      );

      // Send reminder if eligible and profile is incomplete
      if (completenessResult.shouldSendReminder) {
        const userData = await usersApi.getById(user.id) as any;
        
        await emailAPI.sendProfileReminder(user.email || '', {
          firstName: userData?.first_name || 'there',
          userType: userType,
          missingItems: completenessResult.missingItems,
        });

        console.log('Profile reminder sent for incomplete profile');
      }
    } catch (error) {
      console.error('Error checking profile completeness:', error);
    }
  }, [user, userType]);

  return { checkProfileCompleteness };
};

// Hook for job-related emails
export const useJobEmails = () => {
  const sendJobMatchNotification = useCallback(
    async (filmmakerEmail: string, jobData: JobData) => {
      try {
        const result = await emailAPI.sendJobMatchNotification(
          filmmakerEmail,
          jobData
        );

        if (!result.success) {
          console.error('Failed to send job match notification:', result.error);
        }
      } catch (error) {
        console.error('Error sending job match notification:', error);
      }
    },
    []
  );

  const sendApplicationConfirmation = useCallback(
    async (filmmakerEmail: string, applicationData: ApplicationData) => {
      try {
        const result = await emailAPI.sendApplicationSubmittedConfirmation(
          filmmakerEmail,
          applicationData
        );

        if (result.success) {
          toast.success('Application submitted! Confirmation email sent.');
        } else {
          toast.error('Application submitted, but email notification failed.');
        }
      } catch (error) {
        console.error('Error sending application confirmation:', error);
      }
    },
    []
  );

  const sendApplicationReceivedNotification = useCallback(
    async (
      clientEmail: string,
      applicationData: {
        clientName: string;
        filmmakerName: string;
        jobTitle: string;
        bidAmount: number;
        coverLetterExcerpt: string;
        timestamp: string;
        applicationUrl: string;
        // Navigation IDs for clickable notifications
        job_id?: string;
        bid_id?: string;
      }
    ) => {
      try {
        const result = await emailAPI.sendApplicationReceivedNotification(
          clientEmail,
          applicationData
        );

        if (!result.success) {
          console.error(
            'Failed to send application received notification:',
            result.error
          );
        }
      } catch (error) {
        console.error(
          'Error sending application received notification:',
          error
        );
      }
    },
    []
  );

  return {
    sendJobMatchNotification,
    sendApplicationConfirmation,
    sendApplicationReceivedNotification,
  };
};

// Hook for booking emails
export const useBookingEmails = () => {
  const sendBookingConfirmation = useCallback(
    async (recipientEmail: string, bookingData: BookingData) => {
      try {
        const result = await emailAPI.sendBookingConfirmation(
          recipientEmail,
          bookingData
        );

        if (result.success) {
          toast.success('Booking confirmed! Email notification sent.');
        } else {
          console.error('Failed to send booking confirmation:', result.error);
        }
      } catch (error) {
        console.error('Error sending booking confirmation:', error);
      }
    },
    []
  );

  return { sendBookingConfirmation };
};

// Hook for message notifications
export const useMessageEmails = () => {
  const { user } = useAuth();

  const sendMessageNotification = useCallback(
    async (
      recipientEmail: string,
      messageData: {
        senderName: string;
        messagePreview: string;
        context?: string;
        recipientRole?: 'client' | 'filmmaker';
        // Navigation IDs for clickable notifications
        room_name?: string;
        sender_id?: string;
        sender_role?: string;
      }
    ) => {
      try {
        const result = await emailAPI.sendMessageNotification(
          recipientEmail,
          messageData
        );

        if (!result.success && result.error) {
          console.error('Failed to send message notification:', result.error);
        }
      } catch (error) {
        console.error('Error sending message notification:', error);
      }
    },
    []
  );

  // Track user activity to determine when to send notifications
  const updateUserActivity = useCallback(() => {
    localStorage.setItem('lastActivity', Date.now().toString());
  }, []);

  useEffect(() => {
    // Track user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
    ];

    events.forEach((event) => {
      document.addEventListener(event, updateUserActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, updateUserActivity, true);
      });
    };
  }, [updateUserActivity]);

  return { sendMessageNotification };
};

// Hook for review requests
export const useReviewEmails = () => {
  const sendReviewRequest = useCallback(
    async (
      userEmail: string,
      reviewData: {
        userName: string;
        counterpartyName: string;
        jobTitle: string;
      }
    ) => {
      try {
        const result = await emailAPI.sendReviewRequest(userEmail, reviewData);

        if (!result.success) {
          console.error('Failed to send review request:', result.error);
        }
      } catch (error) {
        console.error('Error sending review request:', error);
      }
    },
    []
  );

  return { sendReviewRequest };
};

// Hook for weekly digest emails
export const useWeeklyDigest = () => {
  const { user, userType } = useAuth();

  const scheduleWeeklyDigest = useCallback(async () => {
    if (!user || !userType) return;

    try {
      // Only for filmmakers and clients, not admins
      if (userType === 'filmmaker' || userType === 'client') {
        // Get user data using users API
        const userData = await usersApi.getById(user.id) as any;

        if (!userData) return;

        // Calculate proper week number using ISO week standard
        const now = new Date();
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const days = Math.floor(
          (now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000)
        );
        const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);

        // Check if we've already scheduled this week's digest
        const weekKey = `digest_${user.id}_${now.getFullYear()}_week_${weekNumber}`;
        const hasScheduled = localStorage.getItem(weekKey);

        if (hasScheduled) return;

        // Only schedule if user has been active recently and has complete profile
        const lastActivity = localStorage.getItem('lastActivity');
        const fourteenDaysAgo = Date.now() - 14 * 24 * 60 * 60 * 1000;

        // Don't send weekly digest to completely inactive users
        if (!lastActivity || parseInt(lastActivity) < fourteenDaysAgo) {
          return;
        }

        // Schedule for next Tuesday at 10 AM
        const nextTuesday = new Date();
        const daysUntilTuesday = (2 - nextTuesday.getDay() + 7) % 7;
        if (daysUntilTuesday === 0) {
          // If today is Tuesday, schedule for next Tuesday
          nextTuesday.setDate(nextTuesday.getDate() + 7);
        } else {
          nextTuesday.setDate(nextTuesday.getDate() + daysUntilTuesday);
        }
        nextTuesday.setHours(10, 0, 0, 0);

        // Get digest data based on user type
        const digestData = {
          firstName: userData.first_name,
          jobMatches: [],
          profileStats: {
            views: 0,
            viewsChange: 0,
            searches: 0,
            successRate: 0,
          },
        };

        if (userType === 'filmmaker') {
          // Get job matches for filmmaker
          // This would need to be implemented based on your job matching logic
        }

        await emailAPI.scheduleEmail(
          {
            emailType: 'weekly_digest',
            recipientEmail: user.email || '',
            templateData: digestData,
            priority: 'low',
          },
          nextTuesday
        );

        localStorage.setItem(weekKey, 'true');
        console.log(
          'Weekly digest scheduled for',
          nextTuesday.toLocaleString()
        );
      }
    } catch (error) {
      console.error('Error scheduling weekly digest:', error);
    }
  }, [user, userType]);

  return { scheduleWeeklyDigest };
};

// Hook for payment reminder emails
export const usePaymentEmails = () => {
  const sendPaymentReminder = useCallback(
    async (
      clientEmail: string,
      reminderData: {
        clientName: string;
        filmmakerName: string;
        jobTitle: string;
        jobAmount: number;
        daysOverdue: number;
        jobId: string;
      }
    ) => {
      try {
        const result = await emailAPI.sendPaymentReminderToClient(
          clientEmail,
          reminderData
        );

        if (result.success) {
          toast.success('Payment reminder sent to client.');
        } else {
          console.error('Failed to send payment reminder:', result.error);
        }
      } catch (error) {
        console.error('Error sending payment reminder:', error);
      }
    },
    []
  );

  return { sendPaymentReminder };
};

// Hook for payout notification emails
export const usePayoutEmails = () => {
  const sendPayoutNotification = useCallback(
    async (
      filmmakerEmail: string,
      payoutData: {
        filmmakerName: string;
        clientName: string;
        jobTitle: string;
        grossAmount: number;
        platformFee: number;
        netAmount: number;
        payoutMethod: string;
        transactionId: string;
        estimatedArrival: string;
      }
    ) => {
      try {
        const result = await emailAPI.sendPayoutNotificationToFilmmaker(
          filmmakerEmail,
          payoutData
        );

        if (result.success) {
          toast.success('Payout notification sent to filmmaker.');
        } else {
          console.error('Failed to send payout notification:', result.error);
        }
      } catch (error) {
        console.error('Error sending payout notification:', error);
      }
    },
    []
  );

  return { sendPayoutNotification };
};

// Hook for deliverable emails
export const useDeliverableEmails = () => {
  const sendDeliverableSubmitted = useCallback(
    async (
      clientEmail: string,
      deliverableData: {
        clientName: string;
        filmmakerName: string;
        jobTitle: string;
        deliverableTitle: string;
        submissionMessage?: string;
        reviewDeadline?: string;
      }
    ) => {
      try {
        const result = await emailAPI.sendDeliverableSubmitted(
          clientEmail,
          deliverableData
        );

        if (result.success) {
          console.log('Deliverable submission notification sent to client.');
        } else {
          console.error(
            'Failed to send deliverable submission notification:',
            result.error
          );
        }
      } catch (error) {
        console.error(
          'Error sending deliverable submission notification:',
          error
        );
      }
    },
    []
  );

  const sendDeliverableApproved = useCallback(
    async (
      filmmakerEmail: string,
      deliverableData: {
        filmmakerName: string;
        clientName: string;
        jobTitle: string;
        deliverableTitle: string;
        approvalMessage?: string;
        nextSteps?: string;
      }
    ) => {
      try {
        const result = await emailAPI.sendDeliverableApproved(
          filmmakerEmail,
          deliverableData
        );

        if (result.success) {
          toast.success('Deliverable approval notification sent to filmmaker.');
        } else {
          console.error(
            'Failed to send deliverable approval notification:',
            result.error
          );
        }
      } catch (error) {
        console.error(
          'Error sending deliverable approval notification:',
          error
        );
      }
    },
    []
  );

  const sendDeliverableRevisionRequested = useCallback(
    async (
      filmmakerEmail: string,
      deliverableData: {
        filmmakerName: string;
        clientName: string;
        jobTitle: string;
        deliverableTitle: string;
        revisionNotes: string;
        revisionDeadline?: string;
      }
    ) => {
      try {
        const result = await emailAPI.sendDeliverableRevisionRequested(
          filmmakerEmail,
          deliverableData
        );

        if (result.success) {
          toast.success('Revision request notification sent to filmmaker.');
        } else {
          console.error(
            'Failed to send revision request notification:',
            result.error
          );
        }
      } catch (error) {
        console.error('Error sending revision request notification:', error);
      }
    },
    []
  );

  return {
    sendDeliverableSubmitted,
    sendDeliverableApproved,
    sendDeliverableRevisionRequested,
  };
};

// Hook for verification reminder emails
export const useVerificationEmails = () => {
  const sendVerificationReminder = useCallback(
    async (
      userEmail: string,
      reminderData: {
        firstName: string;
        userType: string;
        daysSinceSignup: number;
        verificationUrl: string;
      }
    ) => {
      try {
        const result = await emailAPI.sendVerificationReminder(
          userEmail,
          reminderData
        );

        if (result.success) {
          console.log('Verification reminder sent successfully.');
        } else {
          console.error('Failed to send verification reminder:', result.error);
        }
      } catch (error) {
        console.error('Error sending verification reminder:', error);
      }
    },
    []
  );

  return { sendVerificationReminder };
};

// Hook for account approval emails
export const useApprovalEmails = () => {
  const sendAccountApproved = useCallback(
    async (
      userEmail: string,
      approvalData: {
        firstName: string;
        userType: string;
        approvedBy: string;
        approvalDate: string;
        nextSteps?: string[];
      }
    ) => {
      try {
        const result = await emailAPI.sendAccountApproved(
          userEmail,
          approvalData
        );

        if (result.success) {
          toast.success('Account approval notification sent to user.');
        } else {
          console.error(
            'Failed to send account approval notification:',
            result.error
          );
        }
      } catch (error) {
        console.error('Error sending account approval notification:', error);
      }
    },
    []
  );

  return { sendAccountApproved };
};

// Hook for review reminder emails
export const useReviewReminderEmails = () => {
  const sendReviewReminder = useCallback(
    async (
      userEmail: string,
      reviewData: {
        userName: string;
        counterpartyName: string;
        counterpartyRole: string;
        jobTitle: string;
        completionDate: string;
        reviewUrl: string;
        daysSinceCompletion: number;
      }
    ) => {
      try {
        const result = await emailAPI.sendReviewReminder(userEmail, reviewData);

        if (result.success) {
          console.log('Review reminder sent successfully.');
        } else {
          console.error('Failed to send review reminder:', result.error);
        }
      } catch (error) {
        console.error('Error sending review reminder:', error);
      }
    },
    []
  );

  return { sendReviewReminder };
};

// Hook to automatically trigger appropriate emails based on events
export const useAutoEmailTriggers = () => {
  const { sendWelcomeEmail } = useWelcomeEmail();
  const { user } = useAuth();

  // Track user activity for message notification timing
  useEffect(() => {
    if (user) {
      // Update last activity timestamp for message notification logic
      localStorage.setItem('lastActivity', Date.now().toString());
    }
  }, [user]);

  return {
    sendWelcomeEmail, // For manual use during signup
  };
};
